// Puente de SOLO LECTURA hacia `change-mgmt` para el diálogo de selección de change request (UC-30,
// spec.md §4, plan.md §3.3, tasks.md T32). Todo el acceso a red vive en infrastructure/ (regla #3).
// §7-I: `change-mgmt` aún no está migrado — puente `zod`-ligero autocontenido, mismo precedente que
// el resto de repos puente de esta fase. Condición de retiro: cuando migre `change-mgmt`.
//
// Endpoint real observado (`src/api/baselineSnapshots.api.js:16`,
// `ChangeRequestDialog.jsx:55-66`): `/scheduled-activities/search-for-baseline` — se preserva el
// endpoint real (no `/baseline-snapshots/...`, pese al nombre del módulo de origen), fidelidad de
// spec.md como oráculo de regresión.
//
// T59 (migración v1): ruta confirmada en specs/api/v1-endpoints.txt:250 y
// core-manage-api-v1.json:42457 (`get_scheduled_activities_search_for_baseline`). Respuesta 200:
// `data` es directamente `BaselineActivitySearchItemDto[]` (core-manage-api-v1.json:1393-1432),
// wire camelCase — `id/changeRequestNumber/status/activityText/platformCategoryName/
// platformName/specialistName/startAt/vmCount`, TODOS con equivalente confirmado (sin gaps de
// campo, a diferencia de los otros 2 archivos de esta tarea). Los `parameters` documentados del
// endpoint solo listan `search`/`page`/`pageSize` genéricos (`ScheduledActivityListFiltersDto` no
// está enlazada aquí) — `client_id`/`status`/`platform_category_id`/`start_date`/`end_date` se
// siguen enviando tal cual (decisión conservadora: no se puede confirmar si el handler real los
// acepta sin estar documentado, y quitarlos sin evidencia sería más arriesgado que mantenerlos).
// El dominio conserva `start_timestamp` (nombre legado) aunque el wire real es `startAt` —mismo
// criterio que T31 `sala`→`room` en rutas: el nombre público no tiene que calcar el wire.

import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';

const IdSchema = z.union([z.string(), z.number()]).transform(String);

// Forma mínima consumida por la tabla de selección de `ChangeRequestDialog.jsx:220-267` — no
// replica más campos del backend que los que esa vista realmente pinta.
const ChangeRequestOptionDtoSchema = z.object({
  id: IdSchema,
  change_request_number: z.string(),
  status: z.string(),
  activity_text: z.string().nullish(),
  platform_category_name: z.string().nullish(),
  platform_name: z.string().nullish(),
  specialist_name: z.string().nullish(),
  start_at: z.string().nullish(),
  vm_count: z.number().nullish(),
});

export interface ChangeRequestOption {
  id: string;
  change_request_number: string;
  status: string;
  activity_text?: string | null;
  platform_category_name?: string | null;
  platform_name?: string | null;
  specialist_name?: string | null;
  start_timestamp?: string | null;
  vm_count?: number | null;
}

function toChangeRequestOption(
  dto: z.infer<typeof ChangeRequestOptionDtoSchema>
): ChangeRequestOption {
  return {
    id: dto.id,
    change_request_number: dto.change_request_number,
    status: dto.status,
    activity_text: dto.activity_text ?? undefined,
    platform_category_name: dto.platform_category_name ?? undefined,
    platform_name: dto.platform_name ?? undefined,
    specialist_name: dto.specialist_name ?? undefined,
    start_timestamp: dto.start_at ?? undefined,
    vm_count: dto.vm_count ?? undefined,
  };
}

export interface SearchChangesParams {
  client_id: string;
  search?: string;
  status?: string;
  platform_category_id?: number | string;
  start_date?: string;
  end_date?: string;
}

export const changeRequestRepository = {
  // UC-30 (`ChangeRequestDialog.jsx:55-66`): arma los params de búsqueda, incluyendo solo los
  // opcionales realmente presentes (`undefined` ⇒ ausente, se omite la clave por completo — no se
  // envía `{search: undefined}`).
  async searchChanges(params: SearchChangesParams): Promise<ChangeRequestOption[]> {
    const { client_id, search, status, platform_category_id, start_date, end_date } = params;
    const query: Record<string, string | number> = { client_id };
    if (search !== undefined) query.search = search;
    if (status !== undefined) query.status = status;
    if (platform_category_id !== undefined) query.platform_category_id = platform_category_id;
    if (start_date !== undefined) query.start_date = start_date;
    if (end_date !== undefined) query.end_date = end_date;

    const { data } = await apiClient.get('/scheduled-activities/search-for-baseline', {
      params: query,
    });
    return z
      .array(ChangeRequestOptionDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toChangeRequestOption);
  },
};
